import React, { useState } from 'react';
import { track } from '@trello/analytics';
import cx from 'classnames';

import { forTemplate } from 'app/common/lib/util/i18n';
import styles from './Feedback.less';
import { MaxlengthTextarea } from 'app/src/components/MaxlengthTextarea';

interface FeedbackProps {
  onClose?: () => void;
  surveyName: string;
  placement?: string;
  context?: string;
}

const format = forTemplate('feedback_collectors', {
  returnBlankForMissingStrings: true,
});
const MAX_LENGTH = 128;

export const Feedback: React.FunctionComponent<FeedbackProps> = function Feedback(
  props,
) {
  const { onClose, surveyName, placement, context } = {
    placement: '',
    context: '',
    ...props,
  };

  function maybeFormat(key: string, canUseDefault: boolean = false) {
    return (
      format([surveyName, key]) ||
      (canUseDefault ? format(['defaults', key]) || undefined : undefined)
    );
  }

  const preface = maybeFormat('preface');
  const yesNoQuestion = maybeFormat('yes-no-question');
  const freeformQuestion = maybeFormat('freeform-question');
  const yesText = maybeFormat('yes', true);
  const noText = maybeFormat('no', true);
  const submitText = maybeFormat('submit', true);
  const showYesNo = yesNoQuestion !== undefined;
  const showFreeform = freeformQuestion !== undefined;

  const [freeform, setFreeform] = useState('');
  const [yes, setYes] = useState<boolean | undefined>();

  const submitDisabled =
    yesNoQuestion !== undefined &&
    yes === undefined &&
    showFreeform &&
    freeform.length === 0;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        track(
          surveyName,
          placement,
          freeform,
          context,
          yes === true ? 1 : yes === false ? 0 : undefined,
        );
        if (onClose) {
          onClose();
        }
      }}
    >
      {preface && <p>{preface}</p>}
      {showYesNo && (
        <>
          <label>
            <div className={styles.question}>{yesNoQuestion}</div>
          </label>
          <div className={styles.useful}>
            <button
              type="button"
              className={cx('button', { [styles.selected]: yes === true })}
              onClick={() => setYes(yes === true ? undefined : true)}
            >
              {yesText}
            </button>
            <button
              type="button"
              className={cx('button', { [styles.selected]: yes === false })}
              onClick={() => setYes(yes === false ? undefined : false)}
            >
              {noText}
            </button>
          </div>
        </>
      )}
      {showFreeform && (
        <label>
          <div className={styles.question}>{freeformQuestion}</div>
          <MaxlengthTextarea
            className={styles.freeform}
            maxLength={MAX_LENGTH}
            onChange={(e) => setFreeform(e.target.value)}
            value={freeform}
          />
        </label>
      )}
      <button
        className={cx('button', styles.submit, { disabled: submitDisabled })}
        type="submit"
        disabled={submitDisabled}
      >
        {submitText}
      </button>
    </form>
  );
};
